<template>
  <BlokkliProvider
    v-if="product && product.blokkliProps"
    v-bind="product.blokkliProps"
  >
    <header class="container md:grid md:grid-cols-12 md:mt-20 lg:mt-40">
      <figure class="md:col-span-7 relative aspect-1 w-full">
        <Transition name="product-fade">
          <ProductHeaderGallery
            v-if="variation?.sku"
            :key="renderKey"
            v-model="activeIndex"
            :media="variation.images"
          />
        </Transition>
        <div
          class="z-10 absolute top-1/2 -translate-y-1/2 flex flex-col items-start space-y-5 pointer-events-none"
        >
          <div v-for="item in status" :key="item" class="badge uppercase">
            {{ item }}
          </div>
        </div>
        <ProductMattechLabel v-if="matTech" v-bind="matTech" />
      </figure>

      <ProductHeaderDetails
        :title="product.title"
        :lead="product.teaserText"
        :points="product.productPointsText"
        :delivery="deliveryInformation"
        :sizeguide="product.sizeguide"
        :variation="variation"
        :variations="variations"
        @click-shopify="trackLink('shopify')"
      />
    </header>

    <ProductImageSlider :media="product.detailImages" />

    <BlokkliField name="field_paragraphs" :list="product.paragraphs" />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import {
  Currency,
  type ProductFragment,
  type ProductVariationFragment,
} from '#graphql-operations'
import { INJECTION_PRODUCT_PAGE } from '~/helpers/symbols'
import { falsy } from '~/helpers/type'
import type { AllProductTypeSpecs } from '~~/layers/product/types'

definePageMeta({
  name: 'product-detail',
  path: '/produkte/:category/:product',
  languageMapping: {
    en: '/products/:category/:product',
    fr: '/produits/:category/:product',
  },
  drupalRoute: true,
})

const route = useRoute()
const sku = useQueryString('sku')
const language = useCurrentLanguage()
const { region, isImperial } = useCountry()
const { dataLayerPush } = useAnalyticsTracking()

const { data: query } = await useAsyncGraphqlQuery(
  'routeProductDetail',
  {
    path: route.fullPath,
    imperial: isImperial.value,
  },
  {
    graphqlCaching: {
      client: true,
    },
    transform: function (data) {
      return data.data
    },
  },
)

const drupal = await useDrupalRoute<ProductFragment & AllProductTypeSpecs>(
  query.value,
)

const product = computed<(ProductFragment & AllProductTypeSpecs) | null>(
  () => drupal?.entity.value,
)

provide(INJECTION_PRODUCT_PAGE, product)

/**
 * All available variations.
 */
const variations = computed<ProductVariationFragment[]>(
  () => product.value?.variations || [],
)

/**
 * The currently active product variation.
 */
const variation = computed<ProductVariationFragment | undefined>(
  () =>
    variations.value.find((v) => v.sku === sku.value) ||
    variations.value.find((v) => v.isDefaultVariation),
)

// Key includes SKU so switching sizes always triggers the fade (even when
// sizes share the same images).
const renderKey = computed(
  () =>
    `${variation.value?.sku ?? ''}-${
      variation.value?.images
        ?.map((v) => ('id' in v ? v.id : v.__typename))
        .join('-') ?? ''
    }`,
)

const schemaOrg = computed(() => {
  const price = variation.value?.prices.find(
    (v) => v.currency === Currency.CHF,
  )?.formatted
  const warrantyYears = product.value?.field_pt_shared_warranty_years
  const warranty = warrantyYears
    ? {
        '@type': 'WarrantyPromise',
        durationOfWarranty: {
          '@type': 'QuantitativeValue',
          value: Math.round(warrantyYears * 12).toString(),
          unitCode: 'MON',
        },
      }
    : undefined
  const json = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          manufacturer: 'EXPED',
          name: variation.value?.label,
          description: product.value?.body,
          color: variation.value?.color?.name,
          sku: variation.value?.sku,
          productID: variation.value?.sku,
          offers: price
            ? [
                {
                  price,
                  warranty,
                },
              ]
            : undefined,
        },
      ],
    },
    null,
    2,
  )

  return {
    type: 'application/ld+json' as const,
    innerHTML: json,
  }
})

useHead({
  script: [schemaOrg],
})

// Keep track of the current slide index. We pass it also to the :index prop
// of the slider, so that when the slider is re-rendered (e.g. when changing
// the variation), it starts at the same slide index.
const activeIndex = ref(0)

const matTech = computed(() => product.value?.field_pt_mat_taxref_mattech)

const status = computed(() =>
  (variation.value?.statusTerms || []).map((v) => v.name).filter(falsy),
)

const deliveryInformation = computed(() => {
  const raw = product.value?.field_pt_shared_info
  const terms = Array.isArray(raw) ? raw : raw != null ? [raw] : []
  return terms
    .map((t) => (t && typeof t === 'object' && 'name' in t ? t.name : null))
    .filter(Boolean)
    .join(', ')
})

const trackLink = (linkType: 'shopify') =>
  dataLayerPush({
    event: 'findInStore',
    productID: product.value?.id,
    productSKU: sku.value,
    productName: product.value?.title,
    productType: product.value?.__typename,
    shopType: linkType,
    language: language.value,
    country: region.value,
  })

/**
 * Update the current page links for the product/variation combination.
 */
const updateLanguageLinks = () => {
  if (query.value?.route && 'languageSwitchLinks' in query.value.route) {
    const links = (query.value?.route.languageSwitchLinks || []).reduce<
      Record<string, string>
    >((acc, link) => {
      if (link.language.id && link.url.path) {
        acc[link.language.id] = link.url.path + '?sku=' + sku.value
      }
      return acc
    }, {})
    definePageLanguageLinks(links)
  }
}

// Set links initially.
updateLanguageLinks()

function updatePageState() {
  updateLanguageLinks()
}

// Udpate links when the SKU changes.
watch(sku, updatePageState)

defineOptions({
  name: 'PageProductsDetail',
})

await renderPageDependencies()
</script>
